import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import i18n from 'i18n-js';
import { formatAmount } from 'utils';
import { Table } from 'components/UI';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Can from 'components/Can';
import { Link } from 'react-router-dom';
import { InvoiceItemDetailsModal } from 'components/Modals';
import { linkClassName, linkHostSx } from './Invoice.styled';
import { useSelector } from 'react-redux';

const InvoiceItemsTable = ({
  invoice,
  loading,
  handleEditItem,
  handleCreateItem,
  handleDeleteItem,
  showItemDetailsLink,
}) => {
  const invoiceItems = invoice?.invoice_items || [];

  const [currentInvoiceItem, setCurrentInvoiceItem] = useState(null);
  const [showInvoiceItemDetailModal, setShowInvoiceItemDetailModal] = useState(false);
  const currentOrg = useSelector((state) => state.auth?.organizations?.current);
  const isEmitter = currentOrg?.id === invoice?.account_emitter?.holder_id;
  const allowNewItems = isEmitter;

  const handleCloseModal = () => {
    setCurrentInvoiceItem(null);
    setShowInvoiceItemDetailModal(false);
  };

  const columns = [
    {
      name: i18n.t('tableColumns.name'),
      selector: (row) => row?.invoice_item_type?.name,
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.description'),
      cell: (row) => row?.description || row?.invoice_item_type?.description || '--',
      grow: 1,
    },
    {
      name: i18n.t('invoice.unit_price'),
      selector: (row) => '$' + formatAmount(row?.unit_price || 0),
      grow: 0,
    },
    {
      name: i18n.t('invoice.quantity'),
      selector: (row) =>
        showItemDetailsLink && row?.invoice_item_type?.pricing_type?.internal_items ? (
          <Link
            onClick={() => {
              setCurrentInvoiceItem(row);
              setShowInvoiceItemDetailModal(true);
            }}
            style={{ textTransform: 'capitalize', wordWrap: 'unset' }}
            data-cy={`item-details-${row.id}`}
            className={linkClassName}
            to={'#'}
          >
            {i18n.t(
              `account_statement.${row?.invoice_item_type?.pricing_type?.internal_source_item}_ids`,
              {
                quantity: row?.quantity || 0,
              }
            )}
          </Link>
        ) : (
          <div style={{ whiteSpace: 'break-spaces' }}>
            {`${row?.q_humanized} (${row?.quantity} ${row?.invoice_item_type?.units?.total})`}
          </div>
        ),
      grow: 1,
      style: {
        whiteSpace: 'break-spaces',
      },
    },
    {
      name: i18n.t('invoice.total'),
      selector: (row) => '$' + formatAmount(row?.line_pre_tax || 0),
      grow: 0,
    },
    {
      name: i18n.t('tableColumns.actions'),
      selector: (row, index) =>
        showItemDetailsLink && row?.invoice_item_type?.pricing_type?.internal_items ? (
          i18n.t('common.n/a')
        ) : (
          <Box display="flex" alignItems="center">
            <Can I="update" on="invoice_item">
              <IconButton onClick={() => handleEditItem(row)} data-cy="update-item-btn">
                <EditIcon />
              </IconButton>
            </Can>
            <Can I="delete" on="invoice_item">
              <IconButton
                onClick={() => handleDeleteItem(row.id || index)}
                data-cy="delete-item-btn"
              >
                <DeleteIcon />
              </IconButton>
            </Can>
          </Box>
        ),
      grow: 0,
      omit: !isEmitter,
      minWidth: '9rem',
      center: true,
    },
  ];

  return (
    <Box sx={linkHostSx}>
      {showInvoiceItemDetailModal ? (
        <InvoiceItemDetailsModal
          open={showInvoiceItemDetailModal}
          invoiceItem={currentInvoiceItem}
          handleCloseModal={handleCloseModal}
        />
      ) : null}
      <Box mb={2}>
        <Table data={invoiceItems} columns={columns} loading={loading} pagination={false} />
      </Box>
      {allowNewItems ? (
        <Button
          color="primary"
          startIcon={<AddIcon />}
          data-cy="invoice-add-new-item-btn"
          style={{ textTransform: 'inherit' }}
          onClick={() => handleCreateItem(null)}
        >
          {i18n.t('invoice.add_new_item')}
        </Button>
      ) : (
        <div style={{ height: '2.5rem' }} />
      )}
    </Box>
  );
};

InvoiceItemsTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  invoice: PropTypes.object.isRequired,
  handleEditItem: PropTypes.func.isRequired,
  handleDeleteItem: PropTypes.func.isRequired,
  handleCreateItem: PropTypes.func.isRequired,
  showItemDetailsLink: PropTypes.bool,
};

InvoiceItemsTable.defaultProps = {
  invoice: {},
  loading: false,
  handleEditItem: () => {},
  handleDeleteItem: () => {},
  handleCreateItem: () => {},
};

export default InvoiceItemsTable;
